import type { InterceptedError } from '@/shared/api/intercepted-error';
import {
  Stack,
  Button,
  Group,
  Select,
  ActionIcon,
  Modal,
  TextInput,
  NumberInput,
  Menu,
  Input,
} from '@mantine/core';
import IconDeviceFloppy from '@tabler/icons-react/dist/esm/icons/IconDeviceFloppy.mjs';
import IconEdit from '@tabler/icons-react/dist/esm/icons/IconEdit.mjs';
import IconPlus from '@tabler/icons-react/dist/esm/icons/IconPlus.mjs';
import IconReload from '@tabler/icons-react/dist/esm/icons/IconReload.mjs';
import IconTrash from '@tabler/icons-react/dist/esm/icons/IconTrash.mjs';
import { useState, useMemo, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useSRMViewStore } from '../../model/view-store';
import {
  TQLQueryGraphViewsAPI,
  UpdateTQLQueryGraphView,
} from '@/entities/srm/tql-query';

import { UseFormReturnType } from '@mantine/form';

import {
  useGraphRendererApi,
  GraphViewSnapshotDTO,
  type GraphElements,
} from '@/shared/ui/graph-renderer';
import { useQueryResolver } from '@/shared/lib/hooks/use-query-resolver';

import { ModalFooter } from '@/shared/ui/modal-footer';
import { SystemNameLabel } from '@/shared/ui/system-name-label';
import { omit } from 'lodash';
import { notifications } from '@mantine/notifications';

import { initialGraphViewValues } from '../../model/use-graph-views-form';
import { messageError, requestError } from '@/shared/ui/notifications';

interface Props {
  form: UseFormReturnType<UpdateTQLQueryGraphView>;
  elements: GraphElements;
  selectedLayout: string;
}
export const GraphViewsSettings = ({
  form,
  elements,
  selectedLayout,
}: Props) => {
  const savedViewsId = useId();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    form.setValues(omit(initialGraphViewValues, ['srm_search_query_id']));
    form.resetDirty();
    return () => {
      mounted.current = false;
    };
  }, []);
  const getAPI = useGraphRendererApi();
  const api = getAPI();
  const [pendingView, setPendingView] = useState<Record<
    string,
    unknown
  > | null>(null);
  useEffect(() => {
    if (!pendingView || !api?.views) return;
    try {
      if (api.views.applySnapshot) {
        api.views.applySnapshot(GraphViewSnapshotDTO.parse(pendingView));
      } else {
        api.views.apply(elements, pendingView as GraphElements);
      }
    } catch {
      messageError(t('tqlGraphView.settings.loadFailed', { ns: 'srm' }));
    }
    setPendingView(null);
  }, [api, elements, pendingView]);
  const { t } = useTranslation();

  const { currentTQLQuery } = useSRMViewStore(({ currentTQLQuery }) => ({
    currentTQLQuery,
  }));
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [openedSave, setOpenedSave] = useState<string>(null);

  const { data: views, refetch } = useQueryResolver(
    useQuery({
      queryKey: ['tql-graph-views', currentTQLQuery],
      queryFn: () =>
        TQLQueryGraphViewsAPI.getTQLQueryGraphViews(currentTQLQuery).then(
          (res) => res.data,
        ),

      cacheTime: 0,
      enabled: !!currentTQLQuery,
    }),
    {
      onSuccess: (data) => {
        if (data?.[0]) {
          loadView(data[0]?.id);
        }
      },
      refetchable: false,
    },
  );

  const initView = (data) => {
    if (!data) return;
    setPendingView(data.configuration ?? {});
  };

  const {
    mutate: loadView,
    isLoading: isLoadingLoadView,
    data,
  } = useMutation({
    mutationKey: ['load-tql-graph-view', selectedView],
    mutationFn: (id: string) =>
      TQLQueryGraphViewsAPI.getTQLQueryGraphView(id).then((res) => res.data),
    onError: (error: InterceptedError) => requestError(error),
    onMutate: (id) => {
      if (!getAPI()?.views?.applySnapshot) handleReset();
      const display_order = views?.find((x) => x.id === id)?.display_order ?? 0;
      form.setFieldValue('display_order', display_order);
    },
    onSuccess: (data) => {
      if (!mounted.current) return;
      form.setValues(data);

      setSelectedView(data.id);
      initView(data);
    },
  });

  const { mutate: saveView, isLoading: isSaving } = useMutation({
    mutationKey: ['save-tql-graph-view', selectedView],
    mutationFn: (id?: string) => {
      const api = getAPI();
      if (!api) throw new Error('Graph not ready');

      if (!api.views) throw new Error('Saved views are not supported');
      const saved = api.views.snapshot();
      const configuration = api.views.applySnapshot
        ? GraphViewSnapshotDTO.parse(saved)
        : { ...(form.getValues().configuration ?? {}), ...saved };
      const values = { ...form.getTransformedValues(), configuration };

      if (id) {
        return TQLQueryGraphViewsAPI.updateTQLQueryGraphView(id, values).then(
          (res) => res.data,
        );
      } else {
        return TQLQueryGraphViewsAPI.createTQLQueryGraphView({
          ...values,
        }).then((res) => res.data);
      }
    },
    onError: (error: InterceptedError) => requestError(error),
    onSuccess: (data) => {
      if (!mounted.current) return;
      refetch();
      notifications.show({
        message: t('action.successSaveMsg'),
        color: 'green',
      });
      form.setValues(data);
      setSelectedView(data.id);
      initView(data);
      setOpenedSave(null);
    },
  });

  const { mutate: deleteView, isLoading: isDeleting } = useMutation({
    mutationKey: ['delete-tql-graph-view', selectedView],
    mutationFn: (id?: string) =>
      TQLQueryGraphViewsAPI.deleteTQLQueryGraphView(id).then((res) => res.data),
    onError: (error: InterceptedError) => requestError(error),
    onSuccess: () => {
      if (!mounted.current) return;
      refetch();
      setSelectedView(null);
      handleReset(true);
    },
  });

  const viewOptions = useMemo(() => {
    return views?.map((view) => ({
      value: view.id,
      label: view.label,
    }));
  }, [views]);

  const handleReset = (runLayout?: boolean) => {
    setPendingView(null);
    const api = getAPI();
    if (!api) return;
    form.setValues(omit(initialGraphViewValues, ['srm_search_query_id']));
    api.views?.reset(elements);

    if (runLayout) {
      api.runLayout(
        api.getLayouts().includes(selectedLayout)
          ? selectedLayout
          : api.getLayouts()[0],
      );
    }
  };

  return (
    <Input.Wrapper
      id={savedViewsId}
      label={t('tqlGraphView.settings.savedViews', { ns: 'srm' })}
      className='pointer-events-auto'
    >
      <Group gap='xs' wrap='nowrap' align='flex-end'>
        <Select
          id={savedViewsId}
          data={viewOptions ?? []}
          value={selectedView}
          onChange={(v) => {
            if (v) loadView(v);
            else {
              setSelectedView(null);
              handleReset(true);
            }
          }}
          disabled={isLoadingLoadView}
          clearable
          className='grow'
          data-testid='saved-views-select'
        />
        <Group gap='xs' wrap='nowrap'>
          <ActionIcon
            disabled={!selectedView}
            loading={isDeleting}
            variant='light'
            size={36}
            data-testid='reload-view-btn'
            onClick={() => initView(data)}
          >
            <IconReload size={18} />
          </ActionIcon>
          <ActionIcon
            disabled={!selectedView}
            loading={isDeleting}
            color='red'
            variant='light'
            size={36}
            data-testid='delete-view-btn'
            onClick={() => deleteView(selectedView)}
          >
            <IconTrash size={18} />
          </ActionIcon>
          <Menu
            trigger={'hover'}
            withArrow
            disabled={!selectedView || isSaving}
          >
            <Menu.Target>
              <ActionIcon
                size={36}
                loading={isSaving}
                variant='light'
                data-testid='save-view-btn'
                disabled={!api?.views || !form.getValues().srm_search_query_id}
                onClick={() => {
                  if (!selectedView) {
                    setOpenedSave('create');
                  } else {
                    saveView(selectedView);
                  }
                }}
              >
                <IconDeviceFloppy size={18} />
              </ActionIcon>
            </Menu.Target>
            {currentTQLQuery && (
              <Menu.Dropdown>
                <Menu.Item
                  data-testid='edit-view-menu-item'
                  leftSection={<IconEdit size={18} />}
                  onClick={() => setOpenedSave('update')}
                >
                  {t('action.saveAs')}
                </Menu.Item>
                <Menu.Item
                  data-testid='create-view-menu-item'
                  leftSection={<IconPlus size={18} />}
                  onClick={() => {
                    setOpenedSave('create');
                    const values = {
                      ...omit(form.getTransformedValues(), [
                        'name',
                        'label',
                        'display_order',
                      ]),
                      name: '',
                      label: '',
                      display_order: 0,
                    };
                    form.setValues(values);
                    form.resetDirty(values);
                  }}
                >
                  {t('action.saveAsNew')}
                </Menu.Item>
              </Menu.Dropdown>
            )}
          </Menu>

          <Modal
            title={
              openedSave === 'create'
                ? t('action.saveAsNew')
                : t('action.saveAs')
            }
            opened={!!openedSave}
            onClose={() => setOpenedSave(null)}
          >
            <Stack>
              <TextInput
                key={form.key('label')}
                label={t('text.name')}
                required
                data-testid='view-label-input'
                {...form.getInputProps('label')}
              />

              <TextInput
                key={form.key('name')}
                label={<SystemNameLabel />}
                required
                withAsterisk={false}
                data-testid='view-name-input'
                disabled={openedSave === 'update'}
                {...form.getInputProps('name')}
              />
              <NumberInput
                key={form.key('display_order')}
                label={t('tqlGraphView.settings.displayOrder', {
                  ns: 'srm',
                })}
                data-testid='view-display-order-input'
                {...form.getInputProps('display_order')}
              />
              <ModalFooter>
                <Button
                  loading={isSaving}
                  disabled={!form.isValid() || !form.isDirty()}
                  data-testid='save-view-modal-ok-btn'
                  onClick={() =>
                    saveView(openedSave === 'update' ? selectedView : undefined)
                  }
                  variant='filled'
                >
                  {t('action.ok')}
                </Button>
              </ModalFooter>
            </Stack>
          </Modal>
        </Group>
      </Group>
    </Input.Wrapper>
  );
};